import React from "react";
import PlunkDropDown from "../../components/plunk-dropdown";
import { stateName, BASIS_OPTIONS, titleCase } from "./territory-common";

/*
 * Shared bits for the Territory & Reps redesign (2026-10-09, PlunkDocs
 * mockups/territory-reps-redesign-2026-10-09.html): rounded money, place
 * names beyond the US, house-account detection, the bill-to / ship-to
 * dropdown and a "show a few rows, then Show all N" helper. Styles: tr-*
 * in sass/v2/_territory-redesign.scss.
 */

// Rounded money for read-at-a-glance numbers: $111.3M, $389K, $16,680.
// Exact dollars below $100K, where the extra digits still read easily.
export function money(v) {
  const n = Number(v) || 0;
  if (!n) return "$0";
  const a = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(1)}B`;
  if (a >= 999500) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e5) return `${sign}$${Math.round(a / 1e3)}K`;
  return `${sign}$${Math.round(a).toLocaleString("en-US")}`;
}

// Canadian provinces and Mexican states that show up in dealer addresses.
const OTHER_PLACES = {
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  NT: "Northwest Territories",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
  BCS: "Baja California Sur",
  "B.C.S.": "Baja California Sur",
  BCN: "Baja California",
  "B.C.": "Baja California",
  VI: "U.S. Virgin Islands",
  GU: "Guam"
};
export function placeName(code) {
  const c = String(code || "").trim();
  if (!c) return "";
  const s = stateName(c);
  if (s && s !== c) return s;
  // Some feeds carry numeric region codes (e.g. "4", "32") with no known place behind them.
  if (/^\d+$/.test(c)) return `Region code ${c}`;
  return OTHER_PLACES[c.toUpperCase()] || (c.length > 3 ? titleCase(c) : c.toUpperCase());
}

// "Arizona, California, Texas +8" from a comma list of codes.
export function placeList(csv, show = 3) {
  const codes = (Array.isArray(csv) ? csv : String(csv || "").split(",")).map(s => String(s).trim()).filter(Boolean);
  if (!codes.length) return "";
  // Named places first; bare numeric codes sort last so the visible names are real places.
  const isCode = c => /^\d+$/.test(c);
  const names = codes.slice().sort((a, b) => isCode(a) - isCode(b) || placeName(a).localeCompare(placeName(b))).map(placeName);
  return names.slice(0, show).join(", ") + (names.length > show ? ` +${names.length - show}` : "");
}

// House / no-rep placeholder reps (same rule as the backend's SalesExecutiveService.IsHouseRep).
const HOUSE_RE = /\b(NO\s*REP|HOUSE\s*ACC|HOUSE\s*ACCOUNT|HOUSE|UNASSIGNED)\b/i;
export function isHouseRep(r) {
  if (!r) return false;
  return HOUSE_RE.test(String(r.name || "")) || HOUSE_RE.test(String(r.repCode || ""));
}

// One "rep on the roster" rule for Territory & Reps and the Sales cockpit: not terminated (the backend's
// RepRules.IsTerminated), or sold in the period. Ambella keeps 80 terminated reps on file; counting them made
// Territory say "18 of 115 reps selling" while Sales listed 37 active reps.
export function isTerminatedRep(r) {
  const s = String(r && r.status || "").trim().toLowerCase();
  return s === "terminated" || s === "inactive" || s === "closed";
}
export function isRosterRep(r) {
  return !!r && (!isTerminatedRep(r) || Number(r.orders) > 0 || Number(r.totalAmount || r.revenue) > 0);
}
export function repName(r) {
  if (!r) return "";
  return r.name ? titleCase(r.name) : String(r.repCode || "");
}

// Comparison column label from the global comparison control.
export function vsLabel(comp) {
  return comp === "prev_year" ? "vs last year" : "vs previous period";
}

// Header dropdown: dealers by head office (bill-to) or delivery location (ship-to).
export function BasisSelect({
  value,
  onChange
}) {
  return /*#__PURE__*/React.createElement("span", {
    className: "tr-basis",
    title: "Place each dealer by its head office (bill-to address) or its delivery location (ship-to address)"
  }, /*#__PURE__*/React.createElement(PlunkDropDown, {
    width: "130px",
    value: value,
    onChange: onChange,
    options: BASIS_OPTIONS.map(o => [o.value, o.label])
  }));
}

// Footer for a capped table: an optional note on the left, "Show all N" / "Show fewer" on the right.
export function ShowAll({
  total,
  shown,
  open,
  onToggle,
  note,
  allLabel,
  fewerLabel
}) {
  const canToggle = total > shown || open;
  if (!note && !canToggle) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: "tr-foot"
  }, /*#__PURE__*/React.createElement("span", {
    className: "tr-foot-note"
  }, note || ""), canToggle ? /*#__PURE__*/React.createElement("a", {
    className: "tr-link",
    href: "#",
    onClick: e => {
      e.preventDefault();
      onToggle();
    }
  }, open ? fewerLabel || "Show fewer" : allLabel || `Show all ${Number(total).toLocaleString("en-US")}`) : null);
}

// Chip filter row: options = [{ v, l, n }]
export function ChipRow({
  options,
  value,
  onChange
}) {
  return /*#__PURE__*/React.createElement("span", {
    className: "tr-chips"
  }, options.map(o => /*#__PURE__*/React.createElement("button", {
    key: o.v,
    type: "button",
    className: `tr-chip${value === o.v ? " on" : ""}`,
    onClick: () => onChange(o.v)
  }, o.l, o.n != null ? /*#__PURE__*/React.createElement("span", {
    className: "tr-chip-n"
  }, Number(o.n).toLocaleString("en-US")) : null)));
}

// Delta text for a table cell: "+40%", "-12%", "new" (no prior), or a dash.
// isNew = the row had sales now and none in the compared window (only pass it when a comparison is on).
export function DeltaText({
  d,
  isNew
}) {
  if (d && d.pct != null) {
    const p = Math.round(d.pct);
    return /*#__PURE__*/React.createElement("span", {
      className: `tr-delta ${p > 0 ? "up" : p < 0 ? "down" : "flat"}`
    }, `${p > 0 ? "+" : ""}${p}%`);
  }
  if (isNew) return /*#__PURE__*/React.createElement("span", {
    className: "tr-delta flat"
  }, "new");
  return /*#__PURE__*/React.createElement("span", {
    className: "tr-delta flat"
  }, "-");
}